import type { MultiPolygon, Polygon, Position } from "geojson";

/** Bbox-midpoint of a Polygon / MultiPolygon — a good-enough centroid for ag-field shapes.
 *  Deliberately the bounding-box center, not the area-weighted centroid (`@turf/centroid`):
 *  it's cheaper and, for the convex-ish field boundaries we deal with, lands inside the shape
 *  and reads as "the middle" without the pull a true centroid gets from concave edges.
 *
 *  The bbox is computed by hand rather than with `@turf/bbox`: this module sits behind the
 *  `@domain/Geo/geometry` barrel, which first paint loads, and the critical-path gate
 *  (`Web/perf-critical-path.json`) keeps `@turf/bbox` off eager first-party imports. */
export function polygonBboxCenter(geom: Polygon | MultiPolygon): Position {
	const polygons = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	for (const rings of polygons) {
		for (const ring of rings) {
			for (const [lng, lat] of ring) {
				if (lng < minLng) minLng = lng;
				if (lng > maxLng) maxLng = lng;
				if (lat < minLat) minLat = lat;
				if (lat > maxLat) maxLat = lat;
			}
		}
	}
	return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}
